import React, { useCallback } from 'react';
import { Cell, flexRender, Row } from '@tanstack/react-table';
import { ColumnId } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { createStyles } from '@/contexts/theme-context';
import {
  GestureResponderEvent,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import { type OrderEntryTableMeta } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import { type OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import {
  selectSalesPortalCartItem,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableRow } from '@experiences/components/table/table-row';
import { useTableContext } from '@experiences/components/table/blocs/table-context';
import { useTableFocusStore } from '@experiences/components/table/blocs/stores/table-focus-store';

const CONDITIONAL_TEXT_CELL_TOP_SPACING = 14;
const INTERACTIVE_WEB_SELECTOR =
  'input, textarea, select, button, [role="button"], [contenteditable="true"]';

interface OrderEntryTableRowProps {
  /**
   * Rendered index (position in the visible list after sort/filter).
   * Used for keyboard focus tracking and virtualizer measurement.
   * Note: This differs from row.index which is TanStack's original data index.
   */
  index: number;
  /**
   * Invoked when the row's background is pressed. The outer `View` participates
   * in RN's gesture responder negotiation: cell-level interactives (qty/price
   * inputs, quick-action buttons) claim the responder first and the row only
   * fires for taps that land on padding, gaps, or non-interactive cells.
   */
  onRowPress?: (rowData: OrderEntryTableRowData) => void;
  row: Row<OrderEntryTableRowData>;
}

// Memoized row component that subscribes to its own cart status
// This ensures only affected rows re-render when cart changes
export const OrderEntryTableRow = React.memo<OrderEntryTableRowProps>(
  ({ index, onRowPress, row }) => {
    const [styles, theme] = useStyles();
    const { tableId } = useTableContext();
    const variantPackUUID = row.original.variantPackUUID;

    // Cart subscription for row background tint (green = in cart, orange = over-stock).
    // Per-item subscription prevents unnecessary re-renders of other rows
    const cartQuantity = useCartStore(
      (state) =>
        selectSalesPortalCartItem(state, variantPackUUID)?.quantity ?? 0,
    );
    const isInCart = cartQuantity > 0;

    const onHandQuantity = row.original.onHandQuantity;
    const isOverStock =
      onHandQuantity !== null && cartQuantity > onHandQuantity;

    // Subscribe to keyboard focus state for this row only
    // By comparing directly in the selector, only the previously-focused and
    // newly-focused rows re-render (not all rows)
    const isRowFocused = useTableFocusStore(
      (state) => state.focusedIndexByTableId[tableId] === index,
    );

    const activeBorderStyle = isRowFocused ? styles.activeRowBorder : undefined;
    const visibleCells = row.getVisibleCells();
    const rowData = row.original;

    const handlePress = useCallback(() => {
      useTableFocusStore.getState().focusRowVisualOnly(tableId, index);
      onRowPress?.(rowData);
    }, [tableId, index, onRowPress, rowData]);

    const meta: OrderEntryTableMeta = (visibleCells[0]?.getContext().table
      .options.meta as OrderEntryTableMeta) ?? {
      areSubstitutionsEnabled: false,
      isPriceEditingEnabled: false,
    };
    const isPriceEditingEnabled = meta.isPriceEditingEnabled === true;
    const showOverStockTint =
      meta.areSubstitutionsEnabled === true && isOverStock;
    const firstCellIndex = 0;
    const lastCellIndex = visibleCells.length - 1;

    const rowBackgroundColor = showOverStockTint
      ? theme.sk.color.bg.highlight.warning
      : isInCart
        ? theme.sk.color.bg.highlight.success
        : theme.sk.color.bg.primary.default;

    const rowElement = (
      <TableRow
        aria-selected={isRowFocused}
        index={index}
        key={row.id}
        style={StyleSheet.flatten([
          { backgroundColor: rowBackgroundColor },
          styles.baseRowBorder,
          activeBorderStyle,
        ])}
      >
        {visibleCells.map(
          (cell: Cell<OrderEntryTableRowData, unknown>, cellIndex) => {
            const isFirstCell = cellIndex === firstCellIndex;
            const isLastCell = cellIndex === lastCellIndex;
            const columnId = cell.column.id;
            const isInputCell =
              columnId === ColumnId.Price || columnId === ColumnId.GpPercentage;
            const cellStyle = {
              backgroundColor: rowBackgroundColor,
              justifyContent: 'flex-start' as const,
              // conditionally reduce top padding for text cells in price editable order entry tables
              ...(isPriceEditingEnabled && !isInputCell
                ? { paddingTop: CONDITIONAL_TEXT_CELL_TOP_SPACING }
                : {}),
              ...(isRowFocused && Platform.OS === 'web'
                ? {
                    ...(isFirstCell ? styles.activeRowFirstCell : {}),
                    ...(isLastCell ? styles.activeRowLastCell : {}),
                  }
                : {}),
            };

            return (
              <TableCell cell={cell} key={cell.id} style={cellStyle}>
                {flexRender(cell.column.columnDef.cell, {
                  ...cell.getContext(),
                  index,
                })}
              </TableCell>
            );
          },
        )}
      </TableRow>
    );

    if (!onRowPress) {
      return rowElement;
    }
    return (
      <View
        onResponderRelease={handlePress}
        onStartShouldSetResponder={shouldClaimRowTouch}
        /* role="presentation" hides this wrapper from the accessibility tree 
        so the html is still correct and a11y compliant */
        role="presentation"
        style={styles.pressable}
      >
        {rowElement}
      </View>
    );
  },
);

const shouldClaimRowTouch = (e: GestureResponderEvent): boolean => {
  if (Platform.OS === 'web') {
    try {
      const target = e.nativeEvent.target as unknown as HTMLElement | undefined;
      if (target?.closest?.(INTERACTIVE_WEB_SELECTOR)) {
        return false;
      }
    } catch (e) {}
  }
  return true;
};

const useStyles = createStyles((theme) => ({
  activeRowBorder: {
    borderColor: theme.sk.color.border.input.active,
  },
  // Rounded corners are handled by applying border-radius to first/last cells
  activeRowFirstCell: {
    borderBottomLeftRadius: theme.sk.radii.radiusMedium,
    borderTopLeftRadius: theme.sk.radii.radiusMedium,
  },
  activeRowLastCell: {
    borderBottomRightRadius: theme.sk.radii.radiusMedium,
    borderTopRightRadius: theme.sk.radii.radiusMedium,
  },
  // Reserve 1px border on every row so focus border doesn't cause layout shift
  baseRowBorder: {
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderColor: 'transparent',
    borderLeftStyle: 'solid',
    borderLeftWidth: 1,
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    borderTopStyle: 'solid',
    borderTopWidth: 1,
    // On web, use overflow: 'visible' to allow sticky columns to work
    ...Platform.select({
      default: {
        overflow: 'hidden',
      },
      web: {
        overflow: 'visible',
      },
    }),
  },
  pressable: {
    cursor: 'pointer',
    ...Platform.select({ web: { overflow: 'visible' } }),
  },
}));
